import type { Side, StageCard, StageView } from '../types/stage';
import type { Box } from './agendaGeometry';
import { REST_RADIUS } from './particleField';

export interface WallGeometry {
  mode: 'orbit' | 'stack';
  w: number;
  h: number;
  /** Anna's core */
  cx: number;
  cy: number;
  /** Visible radius of Anna's field in this view */
  R: number;
  /** Canvas side; the canvas is sized for full presence and scaled down per view */
  canvas: number;
  scale: number;
  cardW: number;
  /** Vertical band the orbit cards may use */
  top: number;
  bottom: number;
}

export interface Placement {
  /** Card centre, relative to Anna's core */
  x: number;
  y: number;
  scale: number;
  /** 0 = newest, in front */
  depth: number;
  side: Side;
  /** Inner edge of the card, where the filament meets it (x relative to the core) */
  anchorX: number;
}

/** Visible sphere radius as a fraction of the canvas half-size — Anna at rest. */
const FIELD = REST_RADIUS;
/** Safe area: 48 px from every screen edge, plus room for the 7 px pointer parallax. */
export const SAFE = 48;
const EDGE = SAFE + 8;
const MIN_CARD = 330;
const MAX_CARD = 460;
const GAP = 28;
/** Clearance around obstacles (agenda labels, the ring) */
const CLEAR = 18;
const DEPTH_SCALE = [1, 0.95, 0.9];
/** Cards start beyond the satellite orbits and the agenda ring. */
export const ORBIT_GAP = 1.3;

const VIEW: Record<StageView, { s: number; cy: number }> = {
  stage: { s: 1, cy: 0.46 },
  sleep: { s: 1, cy: 0.46 },
  alarm: { s: 0.5, cy: 0.24 },
  approvals: { s: 0.4, cy: 0.2 },
};

/** Anna fills the room: at 1920×1200 her field is 60 % of the screen height. */
export function wallGeometry(w: number, h: number, view: StageView): WallGeometry {
  const top = Math.max(150, SAFE + 100);
  const bottom = h - 140;
  if (w < 1024 || h < 600) {
    const R = Math.min(w * 0.34, h * 0.24);
    return { mode: 'stack', w, h, cx: w / 2, cy: 150 + R, R, canvas: (R * 2) / FIELD, scale: 1, cardW: Math.min(560, w - 32), top, bottom };
  }
  const Rfull = Math.max(150, Math.min(h * 0.3, (w / 2 - EDGE - MIN_CARD - 20) / ORBIT_GAP));
  const cardW = Math.max(MIN_CARD, Math.min(MAX_CARD, w / 2 - EDGE - (Rfull * ORBIT_GAP + 20)));
  const v = VIEW[view];
  const tight = h < 900 && view === 'approvals';
  const s = tight ? 0.32 : v.s;
  const cy = h * (tight ? 0.16 : v.cy);
  return { mode: 'orbit', w, h, cx: w / 2, cy, R: Rfull * s, canvas: (Rfull * 2) / FIELD, scale: s, cardW, top, bottom };
}

/** Minimal distance from the core (along x) at which a card spanning [y0, y1] clears the ring. */
function ringClearance(y0: number, y1: number, ring: number): number {
  const yn = y0 <= 0 && y1 >= 0 ? 0 : Math.min(Math.abs(y0), Math.abs(y1));
  return yn >= ring ? 0 : Math.sqrt(ring * ring - yn * yn);
}

/**
 * Cards orbit Anna left and right, always outside the ring (satellites + agenda arc) and clear of the
 * agenda labels; the filament bridges the gap. Every card stays ≥ 48 px inside the screen.
 * Cards on one side never overlap: each takes the free slot nearest to its ideal position.
 */
export function layoutOrbit(
  cards: StageCard[],
  heights: Record<string, number>,
  g: WallGeometry,
  obstacles: Box[] = [],
): Record<string, Placement> {
  const out: Record<string, Placement> = {};
  const ring = g.R * ORBIT_GAP + 20;
  const limitX = g.w / 2 - EDGE;
  const bandTop = g.top - g.cy;
  const bandBottom = g.bottom - g.cy;

  (['links', 'rechts'] as Side[]).forEach((side) => {
    const list = cards.filter((c) => c.side === side);
    if (!list.length) return;
    const dir = side === 'links' ? -1 : 1;
    const sideObstacles = obstacles
      .map((b) => (dir === 1 ? { o: b.x1, i: b.x0, y0: b.y0, y1: b.y1 } : { o: -b.x0, i: -b.x1, y0: b.y0, y1: b.y1 }))
      .filter((b) => b.o > 0);

    const meta = list.map((c) => {
      const depth = cards.length - 1 - cards.indexOf(c);
      const scale = DEPTH_SCALE[depth] ?? DEPTH_SCALE[DEPTH_SCALE.length - 1];
      return { c, depth, scale, h: (heights[c.uid] ?? 260) * scale, w: g.cardW * scale };
    });
    const total = meta.reduce((s, m) => s + m.h, 0) + GAP * (meta.length - 1);
    let ideal = Math.max(bandTop, Math.min(bandBottom - total, -total / 2 - 16));

    const placed: { x0: number; x1: number; y0: number; y1: number }[] = [];
    const innerFor = (y0: number, y1: number) => {
      let x = ringClearance(y0 - CLEAR, y1 + CLEAR, ring);
      sideObstacles.forEach((b) => {
        if (b.y1 + CLEAR > y0 && b.y0 - CLEAR < y1) x = Math.max(x, b.o + CLEAR);
      });
      return x;
    };
    const free = (x0: number, x1: number, y0: number, y1: number) =>
      placed.every((p) => x1 + GAP / 2 <= p.x0 || x0 - GAP / 2 >= p.x1 || y1 + GAP <= p.y0 || y0 - GAP >= p.y1);

    meta.forEach((m) => {
      let best: { y0: number; x: number } | null = null;
      const maxY0 = Math.max(bandTop, bandBottom - m.h);
      for (let y0 = bandTop; y0 <= maxY0 + 0.5; y0 += 6) {
        const x = innerFor(y0, y0 + m.h);
        if (x + m.w > limitX) continue;
        if (!free(x, x + m.w, y0, y0 + m.h)) continue;
        if (!best || Math.abs(y0 - ideal) < Math.abs(best.y0 - ideal)) best = { y0, x };
      }
      // No clean slot: keep the safe area and stay outside the ring at least.
      const y0 = best ? best.y0 : Math.max(bandTop, Math.min(maxY0, ideal));
      const x = best ? best.x : Math.min(limitX - m.w, Math.max(ring, innerFor(y0, y0 + m.h)));
      placed.push({ x0: x, x1: x + m.w, y0, y1: y0 + m.h });
      out[m.c.uid] = {
        x: dir * (x + m.w / 2),
        y: y0 + m.h / 2,
        scale: m.scale,
        depth: m.depth,
        side,
        anchorX: dir * x,
      };
      ideal = y0 + m.h + GAP;
    });
  });
  return out;
}
